import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  Settings,
  ArrowLeft,
  ShoppingBag,
  Heart,
  FileText,
  Image,
} from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { PageContainer } from 'src/shared/components/PageContainer'
import { useLocalization } from 'src/shared/hooks/useLocalization'

import { TabNavigation, Tab } from 'src/shared/components/TabNavigation'
import { Button } from 'src/shared/components/ui'
import { ExtractCompanyInfo } from './ExtractCompanyInfo'
import { OrganizationSettingsTabs } from 'src/pages/_myAccount/Settings/OrganizationSettingsTabs'
import { RequestsPage } from 'src/pages/_myAccount/Requests'
import { FavouritesPage } from 'src/pages/_myAccount/Favourites'
import { InvoicesPage } from 'src/pages/_myAccount/Invoices'
import { AssetsPage } from 'src/pages/_myAccount/Assets'
import { paths } from 'src/routes/paths'

const ORG_ADMIN_VIEWS = [
  'orders',
  'invoices',
  'assets',
  'favourites',
  'settings',
] as const
type OrgAdminView = (typeof ORG_ADMIN_VIEWS)[number]

export const OrganizationPage = () => {
  const { t } = useLocalization()
  const { org_id } = useParams()
  const { organizationById } = useOrgsApi({ org_id })
  const organization = organizationById?.data
  const [searchParams, setSearchParams] = useSearchParams()

  const isAnon = organization?.name === 'New org'

  const viewParam = searchParams.get('view')
  const activeView: OrgAdminView =
    viewParam && ORG_ADMIN_VIEWS.includes(viewParam as OrgAdminView)
      ? (viewParam as OrgAdminView)
      : 'orders'

  const topTabs: Tab[] = useMemo(
    () => [
      {
        id: 'orders',
        label: t('Orders', 'Ordrar'),
        icon: <ShoppingBag className="h-4 w-4" />,
      },
      {
        id: 'invoices',
        label: t('Invoices', 'Fakturor'),
        icon: <FileText className="h-4 w-4" />,
      },
      {
        id: 'assets',
        label: t('Assets', 'Filer'),
        icon: <Image className="h-4 w-4" />,
      },
      {
        id: 'favourites',
        label: t('Favourites', 'Favoriter'),
        icon: <Heart className="h-4 w-4" />,
      },
      {
        id: 'settings',
        label: t('Settings', 'Inställningar'),
        icon: <Settings className="h-4 w-4" />,
      },
    ],
    [t]
  )

  const setActiveView = (viewId: string) => {
    const newParams = new URLSearchParams(searchParams)
    if (viewId === 'orders') {
      newParams.delete('view')
    } else {
      newParams.set('view', viewId)
    }
    setSearchParams(newParams, { replace: true })
  }

  return (
    <PageContainer>
      <Column className="gap-4">
        <Row className="items-center gap-2">
          <Button onClick={() => window.history.back()} variant="secondary">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('Back', 'Tillbaka')}
          </Button>

          <h2>
            {organization?.name || t('New organization', 'Ny organisation')}
          </h2>
        </Row>

        <TabNavigation
          tabs={topTabs}
          activeTab={activeView}
          onTabChange={setActiveView}
        />

        {activeView === 'settings' && isAnon && organization?.id && (
          <ExtractCompanyInfo organizationId={organization.id} />
        )}

        {activeView === 'orders' && (
          <RequestsPage
            embedded
            detailBasePath={paths.admin}
            detailPath={paths.adminOrders}
          />
        )}
        {activeView === 'invoices' && <InvoicesPage embedded />}
        {activeView === 'assets' && <AssetsPage embedded />}
        {activeView === 'favourites' && <FavouritesPage embedded />}
        {activeView === 'settings' && (
          <OrganizationSettingsTabs teamOrgId={org_id} />
        )}
      </Column>
    </PageContainer>
  )
}
